// Eliminar tienda por id
export const deleteStore = async (storeId: string) => {
  try {
    // Prefer RPC if available
    if ((supabase as any).rpc) {
      try {
        const { data, error } = await supabase.rpc('delete_store', { p_store_id: storeId });
        if (error) throw error;
        return data;
      } catch (rpcErr) {
        console.warn('deleteStore rpc failed, falling back to direct delete:', rpcErr);
      }
    }
    // Fallback: direct delete
    const { data, error } = await supabase.from('stores').delete().eq('id', storeId);
    if (error) {
      console.error('deleteStore error:', error);
      throw error;
    }
    return data;
  } catch (e) {
    console.error('deleteStore failed:', e);
    throw e;
  }
};
import { supabase } from './client';
import { normalizeLettersOnlySlug } from '@/lib/utils';

export interface StoreItem {
  id: string;
  name: string;
  slug: string;
  description?: string | null;
  avatar_url?: string | null;
  owner_id: string;
  created_at: string;
  updated_at: string;
  status?: string;
  is_public?: boolean;
  latitude?: number | null;
  longitude?: number | null;
  location_display_name?: string | null;
  member_count?: number;
  active_product_count?: number;
  pending_order_count?: number;
  start_date?: string | null;
  end_date?: string | null;
}

export const getUserStores = async (): Promise<StoreItem[]> => {
  // Prefer RPC which respects auth.uid() in the DB
  try {
    const { data, error } = await supabase.rpc('get_user_stores');
    if (error) {
      console.error('getUserStores rpc error:', error);
      throw error;
    }
    // The RPC returns columns named store_id/store_name/store_slug;
    // map them to the `StoreItem` shape expected by the UI (id,name,slug)
    const rows: any[] = (data as any) || [];
    return rows.map((r) => ({
      id: r.store_id || r.id,
      name: r.store_name || r.name || '',
      slug: r.store_slug || r.slug || '',
      description: r.description ?? null,
      avatar_url: r.avatar_url ?? null,
      owner_id: r.owner_id || r.owner || null,
      created_at: r.created_at || r.created || '',
      updated_at: r.updated_at || r.updated || '',
      status: r.status ?? undefined,
      is_public: r.is_public,
      latitude: r.latitude ?? null,
      longitude: r.longitude ?? null,
      location_display_name: r.location_display_name ?? null,
      member_count: r.member_count || 1,
      active_product_count: r.active_product_count || 0,
      pending_order_count: r.pending_order_count || 0,
      start_date: r.start_date ?? null,
      end_date: r.end_date ?? null,
    }));
  } catch (e) {
    console.error('getUserStores failed:', e);
    throw e;
  }
};

export const createStore = async (payload: {
  name: string;
  slug: string;
  description?: string | null;
  avatar_url?: string | null;
  cover_image_url?: string | null;
  website_url?: string | null;
  phone_number?: string | null;
  email?: string | null;
  store_type?: string | null;
}): Promise<StoreItem> => {
  try {
    // Ensure we have the current user id when inserting owner_id client-side
    const session = await supabase.auth.getSession();
    const userId = session?.data?.session?.user?.id || null;
    const insertPayload: any = { ...payload };
    if (userId) insertPayload.owner_id = userId;

    // If slug not provided, generate one from the name and ensure uniqueness
    if (!insertPayload.slug || insertPayload.slug.trim() === '') {
      const raw = insertPayload.name || 'store';
      const base = normalizeLettersOnlySlug(raw) || 'store';
      let candidate = base;
      let i = 1;
      // Helper: convert number to alphabetical suffix (1->a, 2->b, ... 27->aa)
      const numberToAlpha = (n: number) => {
        let s = '';
        while (n > 0) {
          n -= 1;
          s = String.fromCharCode(97 + (n % 26)) + s;
          n = Math.floor(n / 26);
        }
        return s;
      };
      // Loop to find a unique slug (max attempts to avoid infinite loop)
      while (i <= 100) {
        // check if slug exists
        const { data: existing, error: checkError } = await supabase.from('stores').select('id').eq('slug', candidate).limit(1).maybeSingle();
        if (checkError) {
          console.warn('createStore: slug uniqueness check failed:', checkError);
          break;
        }
        if (!existing) {
          break; // candidate is unique
        }
        i += 1;
        const suffix = numberToAlpha(i);
        // ensure total length <= 30
        const maxBaseLength = Math.max(1, 30 - suffix.length);
        candidate = (base.slice(0, maxBaseLength) + suffix).slice(0, 30);
      }
      insertPayload.slug = candidate;
    }

    const { data, error } = await supabase.from('stores').insert(insertPayload).select().single();
    if (error) {
      console.error('createStore error:', error, 'payload=', insertPayload);
      throw error;
    }
      // Ensure owner is added as a member (owner role).
      // Prefer calling a SECURITY DEFINER RPC `ensure_store_owner` if available
      // so that RLS policies do not prevent the insert. If the RPC is not
      // present or fails, fall back to a client-side upsert and log the result.
      try {
        if (userId) {
          try {
            const { data: rpcData, error: rpcErr } = await supabase.rpc('ensure_store_owner', { p_store_id: (data as any).id, p_owner_id: userId });
            if (rpcErr) {
              console.warn('createStore: ensure_store_owner rpc failed, falling back to client upsert:', rpcErr);
              // fallthrough to client upsert
            } else {
              // RPC succeeded — membership ensured server-side
              return data as any;
            }
          } catch (e) {
            console.warn('createStore: rpc call threw, falling back to upsert', e);
          }

          const memberPayload = {
            store_id: (data as any).id,
            user_id: userId,
            role: 'owner',
          };
          // Use upsert semantics to avoid duplicate unique errors
          const { error: upsertErr } = await supabase.from('store_members').upsert(memberPayload, { onConflict: ['store_id', 'user_id'] });
          if (upsertErr) {
            console.warn('createStore: client upsert for store_members failed (RLS?)', upsertErr);
          }
        }
      } catch (memberErr) {
        // Log but don't fail store creation if member insert fails due to RLS or other transient reasons
        console.warn('createStore: could not insert store_members owner row:', memberErr);
      }

      return data as any;
  } catch (e) {
    console.error('createStore failed:', e);
    throw e;
  }
};

export const addStoreMember = async (storeId: string, userId: string, role: string = 'staff') => {
  try {
    const { data, error } = await supabase.rpc('add_store_member', { p_store_id: storeId, p_user_id: userId, p_role: role });
    if (error) {
      console.error('addStoreMember rpc error:', error);
      throw error;
    }
    return data;
  } catch (e) {
    console.error('addStoreMember failed:', e);
    throw e;
  }
};

export const transferStoreOwnership = async (storeId: string, newOwnerId: string, reason: string | null = null) => {
  try {
    const { data, error } = await supabase.rpc('transfer_store_ownership', { p_store_id: storeId, p_new_owner_id: newOwnerId, p_reason: reason });
    if (error) {
      console.error('transferStoreOwnership rpc error:', error);
      throw error;
    }
    return data;
  } catch (e) {
    console.error('transferStoreOwnership failed:', e);
    throw e;
  }
};

export const getStoreById = async (id: string) => {
  try {
    const { data, error } = await supabase.from('stores').select('*').eq('id', id).maybeSingle();
    if (error) {
      return null;
    }
    return data as any;
  } catch {
    return null;
  }
};

export const getStoreBySlug = async (slug: string) => {
  try {
    const { data, error } = await supabase.from('stores').select('*, store_members!store_members_store_id_fkey(*)').eq('slug', slug).maybeSingle();
    if (error) {
      console.error('getStoreBySlug error:', error);
      // If policy recursion detected, surface clearer message for debugging
      if ((error as any)?.code === '42P17') {
        console.error('Database policy recursion detected. Please apply supabase_fix_store_members_policies.sql to fix RLS policies.');
      }
      throw error;
    }
    return data as any;
  } catch (e) {
    console.error('getStoreBySlug unexpected error', e);
    throw e;
  }
};

export const getStoreByIdWithMembers = async (id: string) => {
  const { data, error } = await supabase.from('stores').select('*, store_members!store_members_store_id_fkey(*)').eq('id', id).maybeSingle();
  if (error) {
    console.error('getStoreByIdWithMembers error:', error);
    throw error;
  }
  return data as any;
};

export const updateStore = async (id: string, payload: Record<string, any>) => {
  const { data, error } = await supabase.from('stores').update(payload).eq('id', id).select().maybeSingle();
  if (error) {
    console.error('updateStore error:', error, payload);
    throw error;
  }
  return data as any;
};

// Helper to toggle public/private
export const setStorePublic = async (id: string, isPublic: boolean) => {
  return updateStore(id, { is_public: isPublic });
};

export const isUserOwnerOrAdmin = async (storeId: string) => {
  try {
    const session = await supabase.auth.getSession();
    const userId = session?.data?.session?.user?.id || null;
    if (!userId) return false;
    const { data: s, error } = await supabase.from('stores').select('owner_id').eq('id', storeId).maybeSingle();
    if (error) {
      console.warn('isUserOwnerOrAdmin: error fetching store owner', error);
    }
    if (s?.owner_id === userId) return true;
    const { data: members, error: mErr } = await supabase.from('store_members').select('role').eq('store_id', storeId).eq('user_id', userId).limit(1).maybeSingle();
    if (mErr) {
      console.warn('isUserOwnerOrAdmin: error fetching store_members', mErr);
    }
    if (members && ['owner', 'admin'].includes((members as any).role)) return true;
    return false;
  } catch (e) {
    console.error('isUserOwnerOrAdmin failed', e);
    return false;
  }
};

export const isUserMember = async (storeId: string) => {
  try {
    const session = await supabase.auth.getSession();
    const userId = session?.data?.session?.user?.id || null;
    if (!userId) return false;
    const { data, error } = await supabase.from('store_members').select('role').eq('store_id', storeId).eq('user_id', userId).limit(1).maybeSingle();
    if (error) {
      console.warn('isUserMember error:', error);
      return false;
    }
    return !!data;
  } catch (e) {
    console.error('isUserMember failed', e);
    return false;
  }
};

export const removeStoreMember = async (storeId: string, userId: string) => {
  try {
    // Prefer RPC, but fall back to direct delete if RPC not available.
    // Try RPC first (may not exist on older DBs)
    if ((supabase as any).rpc) {
      try {
        const { data, error } = await supabase.rpc('remove_store_member', { p_store_id: storeId, p_user_id: userId });
        if (error) throw error;
        return data;
      } catch (rpcErr) {
        // If RPC doesn't exist or fails, fall back to direct delete below
        console.warn('removeStoreMember rpc failed, falling back to direct delete:', rpcErr);
      }
    }

    const { data, error } = await supabase.from('store_members').delete().match({ store_id: storeId, user_id: userId });
    if (error) {
      console.error('removeStoreMember delete error:', error);
      throw error;
    }
    return data;
  } catch (e) {
    console.error('removeStoreMember failed:', e);
    throw e;
  }
};
